import React, { useContext, useEffect, useRef, useState } from "react";
import ViewerContext from "../../context/ViewerContext";
import loaderHelper from "../loader/loaderHelper";
import axios from "axios";
import {
  JulianDate,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  defined,
} from "cesium";

const baseUrl = process.env.API_GATEWAY || "https://satmon.fly.dev";
let hoverEntities = [];
let isDragging = false;
let abortController;

let appStart = false;

const FetchSatelliteData = () => {
  const {
    setAllData,
    setSatelliteList,
    dataIntervalPeriod,
    setLatestDateFetchTime,
    constellationName,
  } = useContext(ViewerContext);

  const constellationRef = useRef();

  useEffect(() => {
    constellationRef.current = constellationName;
    refreshSatellites();
  }, [constellationName]);

  useEffect(() => {
    var handler = new ScreenSpaceEventHandler(viewer.scene.canvas);

    // When the mouse moves, check for entity label hover
    handler.setInputAction(function (movement) {
      clearLabels();
      var pickedObject = viewer.scene.pick(movement.endPosition);
      if (defined(pickedObject)) {
        let pickedEntity = pickedObject.id;
        if (pickedEntity.label) {
          hoverEntities.push(pickedEntity);
          pickedEntity.label.show = true;
        }
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);

    let timelineBar = document.getElementsByClassName(
      "cesium-viewer-timelineContainer"
    )[0];

    if (timelineBar) {
      timelineBar.addEventListener("mousedown", onStartDrag, false);
      timelineBar.addEventListener("mouseup", onEndDrag, false);
    }

    continuouslyPostService();
  }, []);

  const clearLabels = () => {
    if (hoverEntities.length > 0) {
      hoverEntities.forEach((entity) => {
        entity.label.show = false;
      });
      hoverEntities = [];
    }
  };

  // Function to handle start of dragging
  function onStartDrag() {
    if (isDragging) return; // Prevent multiple calls
    isDragging = true;
    viewer.clock.shouldAnimate = false; // Pause the animation

    // Hide all data sources
    viewer.dataSources._dataSources.forEach((dataSource) => {
      dataSource.show = false;
    });

    let allEntities = viewer.entities.values;
    allEntities.forEach((entity) => {
      entity.show = false;
    });
  }

  // Function to handle end of dragging
  function onEndDrag() {
    if (!isDragging) return; // Prevent multiple calls

    isDragging = false;
    viewer.clock.shouldAnimate = true; // Resume the animation

    onTimelineClickHandler();
  }

  function onTimelineClickHandler() {
    window.viewer.clock.shouldAnimate = true;
    refreshSatellites();
  }

  const refreshSatellites = () => {
    viewer.dataSources.removeAll();
    viewer.entities.removeAll();
    appStart = true;

    if (abortController) {
      // Cancel previous request if it exists
      abortController.abort();
      abortController = null;
    }

    fetchConstellationLinks();
  };

  const getCurrentTimeISO = (timeOffset) => {
    // Get the current simulation time (JulianDate)
    const currentTime = viewer.clock.currentTime;

    //get 1 minute laters data for animation
    const endTimeJulian = JulianDate.addMinutes(
      currentTime,
      timeOffset,
      new JulianDate()
    );

    // Convert JulianDate to ISO 8601 format
    const isoTime = JulianDate.toIso8601(endTimeJulian);

    setLatestDateFetchTime(endTimeJulian);
    return isoTime;
  };

  const fetchConstellationLinks = async (timeOffset = 0) => {
    if (abortController) {
      // Cancel previous request if it exists
      abortController.abort();
    }

    abortController = new AbortController(); // Create a new controller for this request
    const { signal } = abortController; // Extract signal from controller

    if (appStart) loaderHelper().open();

    const time = getCurrentTimeISO(appStart ? 0 : 1);
    try {
      const response = await axios.post(
        baseUrl + "/constellation-links?api_version=0.2",
        {
          celestrak_id: constellationRef.current.id,
          observation_time: time,
        },
        { signal } // Pass signal to axios request
      );

      if (appStart) {
        viewer.dataSources.removeAll();
        viewer.entities.removeAll();
        appStart = false;
        loaderHelper().close();
      }

      setAllData(response.data);
      setSatelliteList(response.data.satellites);
    } catch (error) {
      if (error.code != "ERR_CANCELED")
        console.error("Error fetching satellite data:", error);
    }
  };

  async function continuouslyPostService(url, data) {
    try {
      if (!appStart) await fetchConstellationLinks(1);
    } finally {
      // Call the service again after 5 seconds
      setTimeout(() => continuouslyPostService(), dataIntervalPeriod * 1000);
    }
  }

  return <></>;
};

export default FetchSatelliteData;
